import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";
// import Stepper from "react-stepper-horizontal";
import useTendering from "../../../../hooks/useTendering";
import useQuotation from "../../../../hooks/useQuotation";
import TenderingDetailContext from "../../context";
import { Button } from "../../../../components";
import ItemWorks from "./ItemWorks";
import { numberToOrdinal } from "../../../../helpers/common.helper";
import { toast } from "react-toastify";
import { ObjectId } from "bson";
import ComboBox from "../../../../components/common/ComboBox";
import { useParams, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepButton from "@mui/material/StepButton";
import { SocketContext } from "../../../../contexts/SocketContext";

const assetsUri = import.meta.env.ASSETS_URI;

const PaymentTerms = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    details,
    projectState: { isOwner },
    refresh,
    togglePayment,
  } = useContext(TenderingDetailContext);
  const { socket } = useContext(SocketContext);
  const { submitWork, updateWork, getSignedUrl, approveSubmission } =
    useTendering();
  const { queryQuotation, getQuotation } = useQuotation();
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [steps, setSteps] = useState([]);
  const [currentPaymentTerm, setCurrentPaymentTerm] = useState(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isHaveRejections, setIsHaveRejections] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isCanApprove, setIsCanApprove] = useState(false);
  const [ownerPaymentTermOptions, setOwnerPaymentTermOptions] = useState([]);

  const _refetch = (_limit, _page) => {
    const {
      contractor: { quotation, submissions },
    } = details;

    let _currentPaymentTerm = 0;

    getQuotation(quotation).then((data) => {
      let _steps = [{ title: "Advanced Payment", completed: true }];
      let _ownerTermOptions = [];

      const _paymentTerms = data.paymentTerms.map((paymentTerm, termIndex) => {
        // * Get steps
        if (termIndex === data.paymentTerms.length - 1) {
          _steps.push({
            title: "Final Payment",
            completed: false,
          });

          _ownerTermOptions.push({
            value: termIndex.toString(),
            name: "Final Payment",
          });
        } else {
          _steps.push({
            title: `${numberToOrdinal(termIndex + 1)} Payment`,
            completed: false,
          });

          _ownerTermOptions.push({
            value: termIndex.toString(),
            name: `${numberToOrdinal(termIndex + 1)} Payment`,
          });
        }

        _steps[termIndex + 1].completed = false;

        const submission = submissions.find((s) => s.no === termIndex);

        if (submission && submission.approved) {
          _steps[termIndex + 1].completed = true;
        }

        // * Map subjects and items
        paymentTerm.subjects = paymentTerm.subjects.map((ptSubject) => {
          let _subject = _.cloneDeep(ptSubject);

          _subject.subject = data.subjects.find(
            (s) => s._id === _subject.subject
          );

          _subject.items = _subject.items.map((ptItem) => {
            const _item = _.cloneDeep(ptItem);
            _item.termNo = termIndex;

            let itemData = undefined;

            for (const subject of data.subjects) {
              for (const subjectItem of subject.items) {
                if (subjectItem._id !== ptItem.item) {
                  continue;
                }

                itemData = subjectItem;
              }
            }

            _item.subject = ptSubject.subject;
            _item.item = itemData;

            if (!submission) return _item;

            // * Get current images and documents
            const submissionItem = submission.subjects
              .find((s) => s.subject === ptSubject.subject)
              .items.find((i) => i.item === ptItem.item);

            if (!submissionItem) {
              return _item;
            }

            const _mapFile = (obj) => {
              return {
                ...obj,
                dataUrl: `${assetsUri}/${obj.key}`,
                file: {
                  name: obj.name,
                  type: obj.mimeType,
                  size: obj.size,
                },
              };
            };

            _item.images = (submissionItem.images ?? []).map((image) =>
              _mapFile(image)
            );

            _item.documents = (submissionItem.documents ?? []).map((doc) =>
              _mapFile(doc)
            );

            _item.remark = submissionItem.remark;
            _item.status = submissionItem.status;
            _item.comment = submissionItem.comment;
            _item.status = submissionItem.status;
            _item.rejections = submissionItem.rejections;
            _item.acceptedOn = submissionItem.acceptedOn;

            return _item;
          });

          return _subject;
        });

        paymentTerm.draft = submission?.draft;
        paymentTerm.approved = submission?.approved;

        if (submission?.approved) {
          _currentPaymentTerm = termIndex + 1;
        }

        return paymentTerm;
      });

      setPaymentTerms(_paymentTerms);
      setSteps(_steps);
      setOwnerPaymentTermOptions(_ownerTermOptions);

      const submissionNoParam = searchParams.get("no");
      if (submissionNoParam) {
        setCurrentPaymentTerm(Number(submissionNoParam));
      }

      if (!submissionNoParam) {
        setCurrentPaymentTerm(_currentPaymentTerm);
      }
    });
  };

  useEffect(() => {
    _refetch();
  }, [details]);

  useEffect(() => {
    const no = searchParams.get("no");

    if (!no) return;

    if (currentPaymentTerm === Number(no)) {
      return;
    }

    setCurrentPaymentTerm(Number(no));
  }, [searchParams]);

  useEffect(() => {
    if (currentPaymentTerm === undefined) return;

    setSearchParams({
      no: currentPaymentTerm,
    });
  }, [currentPaymentTerm]);

  useEffect(() => {
    let _hasWaitingAction = false;
    let _hasRejections = false;
    let _isReviewing = false;

    if (paymentTerms.length === 0) return;

    const current = paymentTerms[currentPaymentTerm];

    for (const subject of current.subjects) {
      for (const item of subject.items) {
        if (!item.status) _hasWaitingAction = true;
        if (item.status === "idle") _hasWaitingAction = true;
        if (item.status === "rejected") {
          _hasWaitingAction = true;
          _hasRejections = true;
        }
      }
    }

    if (current?.draft === false) {
      _isReviewing = true;
    }

    if (!_hasRejections && _isReviewing) {
      setIsReviewing(true);
    }

    setIsHaveRejections(_hasRejections);

    setIsCanApprove(!_hasWaitingAction);
  }, [paymentTerms, currentPaymentTerm]);

  /**
   * Upload image/documents and return object for saving
   * @returns
   */
  const _uploadFile = async (_id, key, file) => {
    const originalSignedUrl = await getSignedUrl(key, file.type);

    const uploadOriginalResponse = await fetch(originalSignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: file,
    });

    if (uploadOriginalResponse.status !== 200) {
      toast.error("Unable to upload cover");
      return;
    }

    return {
      _id: _id,
      key,
      name: file.name,
      mimeType: file.type,
      size: file.size,
    };
  };

  const handleSubmitWork = async (draft) => {
    setIsSaving(true);

    await toast.promise(
      new Promise(async (resolve, reject) => {
        const _subjects = [];

        for (const subject of paymentTerms[currentPaymentTerm]?.subjects) {
          const _items = [];

          for (const item of subject.items) {
            const _images = [];
            const _documents = [];

            for (const image of item.images ?? []) {
              if (image._id) {
                _images.push({
                  _id: image._id,
                  key: image.key,
                  name: image.name,
                  mimeType: image.mimeType,
                  size: image.size,
                });

                continue;
              }

              const imageId = new ObjectId();

              const originalKey = `tenderings/${details._id}/contractor/submissions/${currentPaymentTerm}/images/${imageId}`;

              _images.push(await _uploadFile(imageId, originalKey, image.file));
            }

            for (const document of item.documents ?? []) {
              if (document._id) {
                _documents.push({
                  _id: document._id,
                  key: document.key,
                  name: document.name,
                  mimeType: document.mimeType,
                  size: document.size,
                });

                continue;
              }

              const docId = new ObjectId();

              const originalKey = `tenderings/${details._id}/contractor/submissions/${currentPaymentTerm}/documents/${docId}`;

              _documents.push(
                await _uploadFile(docId, originalKey, document.file)
              );
            }

            _items.push({
              item: item.item._id,
              remark: item.remark,
              images: _images,
              documents: _documents,
              status: item.status === "rejected" ? "idle" : item.status,
              rejections: item.rejections,
              acceptedOn: item.acceptedOn,
            });
          }

          _subjects.push({
            subject: subject.subject._id,
            items: _items,
          });
        }

        const body = {
          no: currentPaymentTerm,
          subjects: _subjects,
          draft,
        };

        if (paymentTerms[currentPaymentTerm]?.draft === undefined) {
          await submitWork(details._id, body);
        } else {
          await updateWork(details._id, currentPaymentTerm, body);
        }

        resolve();
      }),
      {
        pending: draft ? "Saving draft" : "Submitting work",
        success: draft ? "Draft saved!" : "Work submitted!",
        error: draft ? "Failed to save draft" : "Failed to submit work",
      }
    );

    setIsSaving(false);
    refresh();
  };

  const handleApproveTermClick = async () => {
    togglePayment(1000);

    // await approveSubmission(details._id, currentPaymentTerm);

    // toast.success("Approved successfully");

    // setPaymentTerms((curr) => {
    //   curr[currentPaymentTerm].approved = true;
    //   return curr;
    // });

    // // TODO: Display payment Form

    // if (currentPaymentTerm === paymentTerms.length - 1) {
    //   // * Final payment already
    //   return;
    // }

    // setCurrentPaymentTerm(currentPaymentTerm + 1);
  };

  const isApproved = paymentTerms[currentPaymentTerm]?.approved;
  const previousApproved =
    currentPaymentTerm === 0
      ? true
      : paymentTerms[currentPaymentTerm - 1]?.approved;

  const renderContractorActions = () => {
    if (previousApproved && !isApproved) {
      if (isDraft || isHaveRejections) {
        return (
          <>
            {!isHaveRejections && (
              <Button
                label="Save draft"
                variant="secondary"
                size="small"
                onClick={() => handleSubmitWork(true)}
                disabled={isSaving}
              />
            )}
            <Button
              label="Submit Work"
              size="small"
              onClick={() => handleSubmitWork(false)}
              disabled={isSaving}
            />
          </>
        );
      }

      return <Button label="Waiting for review" disabled={true} size="small" />;
    }

    return <></>;
  };

  if (!paymentTerms || currentPaymentTerm === undefined) return <></>;

  const isDraft =
    paymentTerms[currentPaymentTerm]?.draft === true ||
    paymentTerms[currentPaymentTerm]?.draft === undefined;

  const isContractorDisplayActions =
    !isOwner && (isDraft || isHaveRejections) ? true : false;

  return (
    <div className="tendering-documents__payment-terms">
      <div className="tendering-documents__payment-terms__header">
        <h2>Payment Terms</h2>
        {isOwner && paymentTerms.length > 0 && (
          <ComboBox
            name="test"
            options={ownerPaymentTermOptions}
            defaultValue={currentPaymentTerm.toString()}
            onChange={(val) => {
              setCurrentPaymentTerm(Number(val.value));
            }}
          />
        )}
      </div>
      {!isOwner && (
        <Box className="tendering-documents__payment-terms__terms">
          <Stepper
            activeStep={currentPaymentTerm + 1}
            alternativeLabel
            nonLinear
          >
            {steps.map((step, stepIndex) => (
              <Step
                key={`step-${stepIndex}`}
                completed={step.completed}
                sx={{
                  "& .MuiStepLabel-root .Mui-completed": {
                    color: "#14D886", // circle color (COMPLETED)
                  },
                  "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel":
                    {
                      color: "#14D886", // Just text label (COMPLETED)
                    },
                  "& .MuiStepLabel-root .Mui-active": {
                    color: "#013565", // circle color (ACTIVE)
                  },
                  "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel":
                    {
                      color: "#013565", // Just text label (ACTIVE)
                    },
                  "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
                    fill: "#fff", // circle's number (ACTIVE)
                  },
                  "& .MuiStepLabel-label": {
                    fontFamily: "urbaan !important",
                    fontSize: 12,
                    color: "#9D9D9D",
                  },
                }}
              >
                <StepButton
                  color="inherit"
                  onClick={() => {
                    if (stepIndex === 0) return;
                    setCurrentPaymentTerm(stepIndex - 1);
                  }}
                >
                  {step.title}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}
      <table className="tendering-documents__payment-terms__table">
        <thead>
          <tr>
            <th style={{ width: "200px" }}>Subject/Item</th>
            <th style={{ width: "90px" }}>Due Date</th>
            <th style={{ width: "20px", textAlign: "right" }}>Percentage</th>
            <th style={{ width: "20px", textAlign: "right" }}>Cost</th>
            <th>Remarks</th>
            {isOwner && (
              <>
                <th style={{ width: "80px" }}>Status</th>
                <th style={{ width: "20px" }} />
              </>
            )}
            {!isOwner && !isDraft && <th style={{ width: "80px" }}>Status</th>}
            {isContractorDisplayActions && <th style={{ width: "20px" }} />}
          </tr>
        </thead>
        <tbody>
          {paymentTerms[currentPaymentTerm]?.subjects.map(
            (ptSubject, ptSubjectIndex) => {
              return (
                <>
                  {ptSubject.subject.name && (
                    <tr
                      style={{
                        background: "rgb(246, 246, 246)",
                      }}
                    >
                      <td
                        colSpan={"100%"}
                        style={{ color: "#013565", fontWeight: "500" }}
                      >
                        {ptSubject.subject.name}
                      </td>
                    </tr>
                  )}
                  {ptSubject.items.map((d, itemIndex) => {
                    return (
                      <ItemWorks
                        termNo={currentPaymentTerm}
                        index={itemIndex}
                        data={d}
                        view={!isDraft}
                        isReviewing={isReviewing}
                        hasRejections={isHaveRejections}
                        isApproved={isApproved}
                        previousApproved={previousApproved}
                        onChange={({ remark, images, documents }) => {
                          const _paymentTerms = _.cloneDeep(paymentTerms);

                          _paymentTerms[currentPaymentTerm].subjects[
                            ptSubjectIndex
                          ].items[itemIndex].remark = remark;
                          _paymentTerms[currentPaymentTerm].subjects[
                            ptSubjectIndex
                          ].items[itemIndex].images = images;
                          _paymentTerms[currentPaymentTerm].subjects[
                            ptSubjectIndex
                          ].items[itemIndex].documents = documents;

                          setPaymentTerms(_paymentTerms);
                        }}
                      />
                    );
                  })}
                </>
              );
            }
          )}
        </tbody>
      </table>
      <div className="tendering-documents__payment-terms__footer">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: "end",
            flexGrow: 1,
          }}
        >
          {isOwner && !paymentTerms[currentPaymentTerm]?.approved && (
            <Button
              label="Approve"
              disabled={!isCanApprove}
              size="small"
              onClick={handleApproveTermClick}
            />
          )}
          {!isOwner && renderContractorActions()}
        </div>
      </div>
    </div>
  );
};

PaymentTerms.propTypes = {};

export default PaymentTerms;
